import { isEmpty } from 'lodash';
import {
  type Control,
  type FieldPathByValue,
  type FieldValues,
  useController,
  useFormContext,
} from 'react-hook-form';
import type { InteractionContact } from '@/common/types/interaction';
import {
  ContactsAutocomplete,
  type Props as ContactsAutocompleteProps,
} from '@/components/ContactsAutocomplete';
import { mapUserIntegrationContactSuggestionToContact } from '@/components/ContactsAutofill';
import { fetchAgentProfile } from '$agent-profiles';
import {
  type ContactSuggestion,
  type ContactSuggestionContact,
  mapAgentProfileToContact,
} from '$contact-suggestions';

/**
 * Minimal shape a form-side contact must have to be a valid `contactPath`
 * target — the autofill fields shared across every `InteractionContact`
 * variant. The runtime allow-list (`k in contactValue`) lets each consumer's
 * empty-contact constant gate which fields actually get autofilled.
 */
export type ContactSuggestionsFieldContactShape = Partial<
  Pick<InteractionContact, 'firstName' | 'lastName' | 'email' | 'cellPhone'>
>;

/**
 * Props removed from the base autocomplete because the field owns them: the
 * input bindings (`inputValue`, `onChange`, `onBlur`, `onClear`,
 * `onSelectOption`) which are wired to RHF internally.
 */
type WrappedAutocompleteProps = Omit<
  ContactsAutocompleteProps,
  'inputValue' | 'onChange' | 'onBlur' | 'onClear' | 'onSelectOption'
>;

export interface ContactSuggestionsFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TContactPath extends FieldPathByValue<
    TFieldValues,
    ContactSuggestionsFieldContactShape
  > = FieldPathByValue<TFieldValues, ContactSuggestionsFieldContactShape>,
  TNamePath extends FieldPathByValue<
    TFieldValues,
    string | null | undefined
  > = FieldPathByValue<TFieldValues, string | null | undefined>,
> extends WrappedAutocompleteProps {
  control?: Control<TFieldValues>;
  /** Path to the contact card root, e.g. `contacts.agent.0`. */
  contactPath: TContactPath;
  /** Path to the input value (typically the contact's `firstName` field). */
  name: TNamePath;
  /**
   * Fires after autofill writes complete with the selected suggestion. Read
   * the final merged contact via `getValues(contactPath)` if your
   * persistence layer needs the post-autofill snapshot.
   */
  onAutofill?: (option: ContactSuggestion) => void;
  /** Fires after the field blurs, with the current name value. */
  onBlur?: (value: string | null) => void;
}

/**
 * First-name combobox with live identity / Google Contacts / Follow Up Boss
 * suggestions. Wraps the shared `<ContactsAutocomplete>` and adds RHF
 * binding plus a multi-field autofill side-effect: the input itself stores
 * `string | null` at `name`, and on selection the component writes
 * additional fields (lastName, email, cellPhone, etc.) to the contact at
 * `contactPath`. Every prop the underlying autocomplete accepts —
 * `includeGoogleContacts`, `excludeAgentIds`, `licenseStates`,
 * `PaperComponent`, … — passes through.
 *
 * Always requires a surrounding `<FormProvider>` because the multi-field
 * autofill writes go through `setValue` / `getValues`. The optional
 * `control` prop is only for type-checking `name` and `contactPath`.
 */
export function ContactSuggestionsField<
  TFieldValues extends FieldValues = FieldValues,
  TContactPath extends FieldPathByValue<
    TFieldValues,
    ContactSuggestionsFieldContactShape
  > = FieldPathByValue<TFieldValues, ContactSuggestionsFieldContactShape>,
  TNamePath extends FieldPathByValue<
    TFieldValues,
    string | null | undefined
  > = FieldPathByValue<TFieldValues, string | null | undefined>,
>({
  control,
  contactPath,
  name,
  label = 'First Name',
  required,
  onAutofill,
  onBlur: consumerOnBlur,
  ...rest
}: ContactSuggestionsFieldProps<TFieldValues, TContactPath, TNamePath>) {
  const { setValue, getValues } = useFormContext<TFieldValues>();
  const {
    field: { value, onChange, onBlur: rhfOnBlur, ref },
    fieldState: { error },
  } = useController({ control, name });
  const stringValue = typeof value === 'string' ? value : '';

  const applyAutofill = (mapped: ContactSuggestionContact) => {
    const contactValue = getValues(contactPath);
    // `k in contactValue` filters the autofill source to only the keys the
    // consumer's contact actually stores — adding a key to the empty-contact
    // constant opts the contact into receiving it.
    const allowed = Object.fromEntries(
      Object.entries(mapped).filter(
        ([k, v]) => k in contactValue && v != null && v !== '',
      ),
    );
    setValue(
      contactPath,
      // Always preserve original contact ID
      { ...contactValue, ...allowed, id: contactValue.id },
      { shouldValidate: true, shouldDirty: true },
    );
  };

  const handleSelect = async (option: ContactSuggestion) => {
    // Autofill is best-effort: swallow profile-lookup failures and skip the
    // writes so the autocomplete input keeps working.
    try {
      if (option.userId) {
        const teamId = option.contactResponse?.teamIdentityId;
        if (!teamId) return;
        const profile = await fetchAgentProfile(option.userId, teamId);
        applyAutofill({
          ...mapAgentProfileToContact(profile),
          userId: option.userId,
          teamId,
          contactSource: 'identity',
        });
      } else if (option.googleId || option.followUpBossId) {
        const mapped = mapUserIntegrationContactSuggestionToContact(option);
        if (option.googleId) {
          applyAutofill({
            ...mapped,
            googleContactId: option.googleId,
            contactSource: 'google',
          });
        } else if (option.followUpBossId) {
          applyAutofill({
            ...mapped,
            followUpBossContactId: option.followUpBossId,
            contactSource: 'followUpBoss',
          });
        }
      }

      onAutofill?.(option);
    } catch {
      // No-op — see comment above.
    }
  };

  return (
    <ContactsAutocomplete
      {...rest}
      name={name}
      label={label}
      required={required}
      inputValue={stringValue}
      inputRef={ref}
      onChange={(e) => onChange(e.target.value || null)}
      onBlur={() => {
        rhfOnBlur();
        consumerOnBlur?.(stringValue || null);
      }}
      onClear={() => onChange(null)}
      onSelectOption={(option) => {
        void handleSelect(option);
      }}
      error={!!error}
      helperText={error?.message}
      highlight={required && isEmpty(stringValue) ? 'secondary' : undefined}
    />
  );
}
